import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  api,
  type LayoutResponse,
  type LayoutTable,
} from '../lib/api.js';
import { SectionManager } from './SectionManager.js';
import { useVenueId } from '../lib/session.js';
import { mirrorRotation, mirrorX, useDirection } from '../lib/direction.js';
import {
  dragToStoredPosition,
  normaliseRotation,
  nudgeToStoredPosition,
  rectsOverlap,
  resizeToStoredSize,
  screenDeltaToCanvas,
  type Rect,
} from '../lib/floorPlanDrag.js';

/**
 * The floor-plan editor.
 *
 * Drag a table to move it, drag its corner to resize, rotate from the panel.
 * Arrow keys nudge, because a manager lining up a row of tables cannot do it
 * with a finger and because this must be usable without a pointer at all.
 *
 * Two things are load-bearing:
 *
 *  1. COORDINATES ARE STORED IN LTR SPACE, ALWAYS. The canvas mirrors in
 *     Arabic, so a drag performed while mirrored is un-mirrored before it is
 *     saved. `floorPlanDrag.ts` owns that arithmetic and is tested on its own,
 *     because getting it wrong corrupts the plan silently and permanently
 *     (D-067).
 *
 *  2. Saves are per table, on drop. A "Save all" button invites a manager to
 *     rearrange the room and then lose it to a closed tab; saving on drop means
 *     the worst case is one table in the wrong place.
 */

const GRID_STEP = 10;
const MIN_SIZE = 40;

type Mode = 'move' | 'resize';

interface PendingDrag {
  tableId: string;
  mode: Mode;
  origin: { x: number; y: number };
  start: Rect;
}

export function FloorPlanEditor() {
  const { t, i18n } = useTranslation();
  const venueId = useVenueId();
  const dir = useDirection();
  const queryClient = useQueryClient();
  const svgRef = useRef<SVGSVGElement>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, Partial<Rect> & { rotation?: number }>>({});
  const [drag, setDrag] = useState<PendingDrag | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [snapToGrid, setSnapToGrid] = useState(true);
  // Held while the field is focused, so the table is not renamed on every
  // keystroke — each one would be a request and a failed uniqueness check.
  const [labelDraft, setLabelDraft] = useState<string | null>(null);
  // null = every floor at once, which is the wrong default for a restaurant on
  // three levels but the right one for a restaurant on one. Resolved below,
  // after the rooms are known.
  const [floor, setFloor] = useState<number | null>(null);
  const [showSections, setShowSections] = useState(false);

  const layout = useQuery({
    queryKey: ['layout', venueId],
    queryFn: () => api.get<LayoutResponse>('/api/v1/floor/layout', { venue_id: venueId }),
  });

  const save = useMutation({
    mutationFn: (input: { id: string } & Partial<Rect> & { rotation?: number }) =>
      api.patch(`/api/v1/tables/${input.id}/position`, {
        venue_id: venueId,
        x: Math.round(input.x ?? 0),
        y: Math.round(input.y ?? 0),
        ...(input.width === undefined ? {} : { width: Math.round(input.width) }),
        ...(input.height === undefined ? {} : { height: Math.round(input.height) }),
        ...(input.rotation === undefined ? {} : { rotation: normaliseRotation(input.rotation) }),
      }),
    onSuccess: () => {
      setStatus(t('settings.saved'));
      void queryClient.invalidateQueries({ queryKey: ['layout', venueId] });
      void queryClient.invalidateQueries({ queryKey: ['floor'] });
    },
    onError: (err: unknown) => {
      // Put the table back where it was. A table that stays where it was
      // dropped but is not saved there is worse than one that snaps back.
      setDraft({});
      setStatus(err instanceof ApiError ? err.message : t('errors.generic'));
    },
  });

  /**
   * Renaming, and the party sizes a table takes.
   *
   * Separate from `save` because it fails differently: a drag cannot collide
   * with anything, but two tables cannot share a name — `UNIQUE (venue_id,
   * label)` — and that 409 needs its own message rather than "could not save".
   */
  const rename = useMutation({
    mutationFn: (input: { id: string; label?: string; min_party?: number; max_party?: number }) =>
      api.patch(`/api/v1/tables/${input.id}`, { venue_id: venueId, ...input }),
    onSuccess: () => {
      setStatus(t('settings.saved'));
      void queryClient.invalidateQueries({ queryKey: ['layout', venueId] });
      void queryClient.invalidateQueries({ queryKey: ['floor'] });
    },
    onError: (err: unknown) => {
      setLabelDraft(null);
      setStatus(err instanceof ApiError ? err.message : t('errors.generic'));
    },
  });

  /**
   * Sections, and the floors they sit on.
   *
   * A floor is not a thing you create — it is the level a section sits on. So
   * "add a floor" is "add a section and say which level it is on", which is
   * also the only order that produces a floor with anything in it.
   */
  const saveSection = useMutation({
    mutationFn: (input: { id?: string } & Record<string, unknown>) =>
      input.id
        ? api.patch(`/api/v1/rooms/${input.id}`, { venue_id: venueId, ...input })
        : api.post('/api/v1/rooms', { venue_id: venueId, ...input }),
    onSuccess: () => {
      setStatus(t('settings.saved'));
      void queryClient.invalidateQueries({ queryKey: ['layout', venueId] });
      void queryClient.invalidateQueries({ queryKey: ['floor'] });
    },
    onError: (err: unknown) =>
      setStatus(err instanceof ApiError ? err.message : t('errors.generic')),
  });

  /** Stored geometry with any in-flight drag applied. */
  const tables: LayoutTable[] = useMemo(
    () =>
      (layout.data?.tables ?? []).map((table) => ({ ...table, ...draft[table.id] })),
    [layout.data, draft],
  );

  const canvas = useMemo(() => {
    const width = Math.max(960, ...tables.map((table) => table.x + table.width + 60));
    const height = Math.max(540, ...tables.map((table) => table.y + table.height + 60));
    return { width, height };
  }, [tables]);

  const selected = tables.find((table) => table.id === selectedId) ?? null;

  /** Tables sharing space with the selection. A warning, never a refusal. */
  const overlapping = useMemo(() => {
    if (!selected) return [];
    return tables
      .filter((table) => table.id !== selected.id && rectsOverlap(selected, table))
      .map((table) => table.label);
  }, [selected, tables]);

  // --- pointer dragging ----------------------------------------------------

  /**
   * The geometry a drag has reached, outside React state.
   *
   * `pointerup` fires immediately after the last `pointermove`, before React
   * has necessarily re-rendered - so a save handler reading component state
   * would read the position from BEFORE the final move and write the table back
   * where it started. The drag would look right on screen and be wrong in the
   * database, which is the worst of both.
   *
   * A ref is written synchronously, so the save always sees where the table
   * actually ended up. An end-to-end test pins this.
   */
  const latestGeometry = useRef<(Partial<Rect> & { rotation?: number }) | null>(null);

  const commit = useCallback(
    (tableId: string, override?: Partial<Rect> & { rotation?: number }) => {
      const table = tables.find((row) => row.id === tableId);
      if (!table) return;
      const geometry = { ...table, ...override };
      save.mutate({
        id: tableId,
        x: geometry.x,
        y: geometry.y,
        width: geometry.width,
        height: geometry.height,
        rotation: geometry.rotation,
      });
    },
    [tables, save],
  );

  useEffect(() => {
    if (!drag) return;

    const rendered = svgRef.current?.getBoundingClientRect();

    // NOTE: this effect re-runs on every pointermove, because `commit` closes
    // over `tables` and the draft changes as the table moves. Resetting the
    // geometry ref HERE would therefore clear it immediately before pointerup
    // and silently skip the save - the table would follow the finger perfectly
    // and snap back on the next reload. It is reset in `beginDrag` instead,
    // which runs exactly once per drag.

    const onMove = (event: PointerEvent) => {
      const delta = screenDeltaToCanvas(
        { dx: event.clientX - drag.origin.x, dy: event.clientY - drag.origin.y },
        { width: rendered?.width ?? canvas.width, height: rendered?.height ?? canvas.height },
        canvas,
      );

      const gridStep = snapToGrid ? GRID_STEP : 1;

      const next =
        drag.mode === 'move'
          ? dragToStoredPosition({ start: drag.start, delta, direction: dir, canvas, gridStep })
          : resizeToStoredSize({
              start: drag.start,
              delta,
              direction: dir,
              canvas,
              gridStep,
              minSize: MIN_SIZE,
            });

      latestGeometry.current = next;
      setDraft((current) => ({ ...current, [drag.tableId]: next }));
    };

    const onUp = () => {
      const id = drag.tableId;
      const final = latestGeometry.current;
      setDrag(null);
      // No movement at all - a tap to select, not a drag. Nothing to save.
      if (!final) return;
      commit(id, final);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [drag, dir, canvas, snapToGrid, commit]);

  function beginDrag(event: React.PointerEvent, table: LayoutTable, mode: Mode) {
    event.preventDefault();
    setSelectedId(table.id);
    setStatus(null);
    // Once per drag, so a pointerup with no movement in between is recognised
    // as a tap to select rather than saving a position that never changed.
    latestGeometry.current = null;
    setDrag({
      tableId: table.id,
      mode,
      origin: { x: event.clientX, y: event.clientY },
      start: { x: table.x, y: table.y, width: table.width, height: table.height },
    });
  }

  // --- keyboard ------------------------------------------------------------

  function onKeyDown(event: React.KeyboardEvent, table: LayoutTable) {
    const step = event.shiftKey ? GRID_STEP * 5 : GRID_STEP;

    /**
     * Arrow keys are PHYSICAL, not logical.
     *
     * Right moves the table right on screen in both languages. A key that
     * reversed itself in Arabic would be defensible in theory and maddening in
     * practice - the manager is looking at the screen, not at the reading
     * order. The deltas here are therefore screen-space, and
     * `nudgeToStoredPosition` does the un-mirroring for storage.
     */
    const moves: Record<string, { dx: number; dy: number }> = {
      ArrowRight: { dx: step, dy: 0 },
      ArrowLeft: { dx: -step, dy: 0 },
      ArrowDown: { dx: 0, dy: step },
      ArrowUp: { dx: 0, dy: -step },
    };

    const move = moves[event.key];
    if (!move) return;

    event.preventDefault();
    const next = nudgeToStoredPosition({
      start: { x: table.x, y: table.y, width: table.width, height: table.height },
      dx: move.dx,
      dy: move.dy,
      direction: dir,
      canvas,
      gridStep: snapToGrid ? GRID_STEP : 1,
    });

    setDraft((current) => ({ ...current, [table.id]: next }));
    setSelectedId(table.id);
    save.mutate({ id: table.id, ...next, width: table.width, height: table.height, rotation: table.rotation });
  }

  if (layout.isLoading) {
    return <p className="text-sm text-slate-500">{t('app.loading')}</p>;
  }

  const rooms = layout.data?.rooms ?? [];
  const roomsById = new Map(rooms.map((room) => [room.id, room]));

  // Distinct floors, in building order. Basement first, roof last.
  const floors = [...new Set(rooms.map((r) => r.floor))].sort((a, b) => a - b);
  // A single-floor restaurant never sees a floor control at all: a chooser with
  // one option is a decision nobody has.
  const activeFloor = floor ?? floors[0] ?? 0;

  const roomIdsOnFloor = new Set(
    rooms.filter((r) => r.floor === activeFloor).map((r) => r.id),
  );
  const visibleTables = floors.length > 1
    ? tables.filter((table) => roomIdsOnFloor.has(table.room_id))
    : tables;

  const floorName = (level: number): string => {
    const room = rooms.find((r) => r.floor === level);
    const label = i18n.language === 'ar' ? room?.floor_label_ar : room?.floor_label_en;
    if (label) return label;
    // A number needs saying in words, or "0" reads as an error rather than as
    // the ground floor.
    if (level === 0) return t('settings.groundFloor');
    if (level < 0) return t('settings.basement', { level: Math.abs(level) });
    return t('settings.floorN', { level });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">{t('settings.dragToMove')}</p>

        <div className="flex flex-wrap items-center gap-3">
          {floors.length > 1 ? (
            <div className="flex items-center gap-1" role="group" aria-label={t('settings.floor')}>
              {floors.map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => {
                    setFloor(level);
                    // The selection is a table on the floor being left, and a
                    // panel editing something invisible is how a manager
                    // resizes the wrong table.
                    setSelectedId(null);
                  }}
                  aria-pressed={level === activeFloor}
                  className={[
                    'rounded-md px-2.5 py-1 text-sm font-medium transition-colors',
                    level === activeFloor
                      ? 'bg-brand-teal text-white'
                      : 'border border-slate-200 text-slate-600 hover:bg-slate-50',
                  ].join(' ')}
                >
                  {floorName(level)}
                </button>
              ))}
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => setShowSections((v) => !v)}
            className="rounded-md border border-slate-200 px-2.5 py-1 text-sm text-slate-600 hover:bg-slate-50"
          >
            {t('settings.sections')}
          </button>

          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={snapToGrid}
              onChange={(event) => setSnapToGrid(event.target.checked)}
            />
            {t('settings.snapToGrid')}
          </label>
          {status ? (
            <span className="text-sm text-brand-teal" role="status">
              {status}
            </span>
          ) : null}
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[1fr_240px]">
        <div className="overflow-auto rounded-lg border border-slate-200 bg-white p-2">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${canvas.width} ${canvas.height}`}
            width={canvas.width}
            height={canvas.height}
            className="max-w-full touch-none"
            role="application"
            aria-label={t('settings.floorPlan')}
            data-testid="floor-plan-editor"
          >
            {/* A grid, so a manager can see what snapping is doing. */}
            <defs>
              <pattern id="grid" width={GRID_STEP * 5} height={GRID_STEP * 5} patternUnits="userSpaceOnUse">
                <path
                  d={`M ${GRID_STEP * 5} 0 L 0 0 0 ${GRID_STEP * 5}`}
                  fill="none"
                  className="stroke-slate-100"
                  strokeWidth={1}
                />
              </pattern>
            </defs>
            <rect width={canvas.width} height={canvas.height} fill="url(#grid)" />

            {visibleTables.map((table) => {
              // The mirror, applied at render time only.
              const x = mirrorX(table.x, table.width, canvas.width, dir);
              const rotation = mirrorRotation(table.rotation, dir);
              const isSelected = table.id === selectedId;
              const room = roomsById.get(table.room_id);

              return (
                <g
                  key={table.id}
                  transform={`translate(${x} ${table.y}) rotate(${rotation} ${table.width / 2} ${table.height / 2})`}
                  data-testid="editor-table"
                  data-label={table.label}
                >
                  <rect
                    width={table.width}
                    height={table.height}
                    rx={12}
                    tabIndex={0}
                    role="button"
                    aria-label={`${table.label}${room ? ` · ${room.name_en}` : ''}`}
                    onPointerDown={(event) => beginDrag(event, table, 'move')}
                    onKeyDown={(event) => onKeyDown(event, table)}
                    onFocus={() => setSelectedId(table.id)}
                    className={[
                      'cursor-move outline-none transition-colors',
                      isSelected
                        ? 'fill-brand-teal-light stroke-brand-teal'
                        : 'fill-slate-100 stroke-slate-400 hover:stroke-brand-teal',
                      'stroke-2 focus-visible:stroke-brand-bronze',
                    ].join(' ')}
                  />

                  <g transform={`rotate(${-rotation} ${table.width / 2} ${table.height / 2})`}>
                    <text
                      x={table.width / 2}
                      y={table.height / 2 + 5}
                      textAnchor="middle"
                      className="pointer-events-none fill-slate-900 text-[13px] font-semibold"
                      style={{ direction: 'ltr' }}
                    >
                      {table.label}
                    </text>
                  </g>

                  {/* The resize handle sits on the inline-END corner, so it is
                      under the same thumb in both directions. */}
                  {isSelected ? (
                    <rect
                      x={dir === 'rtl' ? -6 : table.width - 6}
                      y={table.height - 6}
                      width={12}
                      height={12}
                      rx={2}
                      onPointerDown={(event) => beginDrag(event, table, 'resize')}
                      className="cursor-nwse-resize fill-brand-bronze stroke-white"
                      data-testid="resize-handle"
                    />
                  ) : null}
                </g>
              );
            })}
          </svg>
        </div>

        <aside className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3">
          {selected ? (
            <>
              <label className="flex flex-col gap-1 text-xs">
                <span className="text-slate-500">{t('settings.tableName')}</span>
                <input
                  value={labelDraft ?? selected.label}
                  onChange={(event) => setLabelDraft(event.target.value)}
                  onFocus={() => setLabelDraft(selected.label)}
                  onBlur={() => {
                    const next = (labelDraft ?? '').trim();
                    setLabelDraft(null);
                    // Nothing to do for an unchanged or emptied field. Empty is
                    // treated as a cancel rather than an error: a table with no
                    // name cannot be called across a room.
                    if (!next || next === selected.label) return;
                    rename.mutate({ id: selected.id, label: next });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur();
                    if (event.key === 'Escape') {
                      setLabelDraft(null);
                      event.currentTarget.blur();
                    }
                  }}
                  maxLength={20}
                  data-testid="table-label"
                  className="w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono text-sm"
                />
                <span className="text-[11px] text-slate-500">{t('settings.tableNameHint')}</span>
              </label>

              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-slate-500">{t('settings.minParty')}</span>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    defaultValue={selected.min_party}
                    key={`min-${selected.id}-${selected.min_party}`}
                    onBlur={(event) => {
                      const value = Number(event.target.value);
                      if (value && value !== selected.min_party) {
                        rename.mutate({ id: selected.id, min_party: value });
                      }
                    }}
                    className="w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono text-sm"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-slate-500">{t('settings.maxParty')}</span>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    defaultValue={selected.max_party}
                    key={`max-${selected.id}-${selected.max_party}`}
                    onBlur={(event) => {
                      const value = Number(event.target.value);
                      if (value && value !== selected.max_party) {
                        rename.mutate({ id: selected.id, max_party: value });
                      }
                    }}
                    className="w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono text-sm"
                  />
                </label>
              </div>

              <dl className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <dt className="text-slate-500">X</dt>
                  <dd className="font-mono" data-numeric data-testid="selected-x">
                    {Math.round(selected.x)}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Y</dt>
                  <dd className="font-mono" data-numeric>
                    {Math.round(selected.y)}
                  </dd>
                </div>
              </dl>

              <label className="flex flex-col gap-1 text-xs">
                <span className="text-slate-500">{t('settings.rotation')}</span>
                <input
                  type="range"
                  min={0}
                  max={345}
                  step={15}
                  value={selected.rotation}
                  onChange={(event) => {
                    const rotation = normaliseRotation(Number(event.target.value));
                    setDraft((current) => ({
                      ...current,
                      [selected.id]: { ...current[selected.id], rotation },
                    }));
                  }}
                  onPointerUp={() => commit(selected.id)}
                  className="accent-brand-teal"
                />
                <span className="font-mono" data-numeric>
                  {selected.rotation}
                </span>
              </label>

              {/* Typed, not only dragged. A corner handle is right for
                  arranging a room by eye; a number is right for making the six
                  tables along a wall identical, which is a thing you cannot do
                  by hand and can see immediately when it is wrong. */}
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-slate-500">{t('settings.width')}</span>
                  <input
                    type="number"
                    min={20}
                    max={600}
                    step={5}
                    key={`w-${selected.id}-${Math.round(selected.width)}`}
                    defaultValue={Math.round(selected.width)}
                    data-testid="table-width"
                    onBlur={(event) => {
                      const width = Number(event.target.value);
                      if (width >= 20 && width !== Math.round(selected.width)) {
                        commit(selected.id, { width });
                      }
                    }}
                    className="w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono text-sm"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs">
                  <span className="text-slate-500">{t('settings.height')}</span>
                  <input
                    type="number"
                    min={20}
                    max={600}
                    step={5}
                    key={`h-${selected.id}-${Math.round(selected.height)}`}
                    defaultValue={Math.round(selected.height)}
                    data-testid="table-height"
                    onBlur={(event) => {
                      const height = Number(event.target.value);
                      if (height >= 20 && height !== Math.round(selected.height)) {
                        commit(selected.id, { height });
                      }
                    }}
                    className="w-full rounded-md border border-slate-300 px-2 py-1.5 font-mono text-sm"
                  />
                </label>
              </div>


              {/* A warning, not a block. A banquette really can sit against a
                  wall table, and an editor that refuses cannot draw the actual
                  restaurant. */}
              {overlapping.length > 0 ? (
                <p className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
                  {t('settings.overlaps', { tables: overlapping.join(', ') })}
                </p>
              ) : null}

              <p className="text-xs text-slate-500">{t('settings.keyboardHint')}</p>
            </>
          ) : (
            <p className="text-sm text-slate-500">{t('settings.selectTable')}</p>
          )}
        </aside>
      </div>
    </div>
  );
}
